import { useState, useEffect } from 'react';
import { Trash2, RefreshCw, Database, Smartphone, Server, AlertTriangle, CheckCircle, XCircle, HardDrive } from 'lucide-react';
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from '../utils/toast';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface CacheStatus {
  frontend: 'idle' | 'clearing' | 'success' | 'error';
  backend: 'idle' | 'clearing' | 'success' | 'error';
  reinstall: 'idle' | 'processing' | 'success' | 'error';
}

interface CacheSizes {
  localStorage: number;
  sessionStorage: number;
  indexedDB: number;
  cacheAPI: number;
  total: number;
}

export default function CacheManagementPage() {
  const { isAdmin } = useAuth();
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    frontend: 'idle',
    backend: 'idle',
    reinstall: 'idle'
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cacheSizes, setCacheSizes] = useState<CacheSizes>({
    localStorage: 0,
    sessionStorage: 0,
    indexedDB: 0,
    cacheAPI: 0,
    total: 0
  });

  // Calculate cache sizes
  const calculateCacheSizes = async () => {
    let localStorageSize = 0;
    let sessionStorageSize = 0;
    let indexedDBSize = 0;
    let cacheAPISize = 0;

    // Calculate localStorage size
    try {
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          localStorageSize += localStorage[key].length + key.length;
        }
      }
    } catch (error) {
      console.warn('Could not calculate localStorage size:', error);
    }

    // Calculate sessionStorage size
    try {
      for (let key in sessionStorage) {
        if (sessionStorage.hasOwnProperty(key)) {
          sessionStorageSize += sessionStorage[key].length + key.length;
        }
      }
    } catch (error) {
      console.warn('Could not calculate sessionStorage size:', error);
    }

    // Calculate IndexedDB size (approximate)
    try {
      if ('indexedDB' in window && 'databases' in indexedDB) {
        const databases = await indexedDB.databases();
        indexedDBSize = databases.length * 1024; // Rough estimate
      }
    } catch (error) {
      console.warn('Could not calculate IndexedDB size:', error);
    }

    // Calculate Cache API size (approximate)
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        cacheAPISize = cacheNames.length * 1024; // Rough estimate
      }
    } catch (error) {
      console.warn('Could not calculate Cache API size:', error);
    }

    const total = localStorageSize + sessionStorageSize + indexedDBSize + cacheAPISize;
    
    setCacheSizes({
      localStorage: localStorageSize,
      sessionStorage: sessionStorageSize,
      indexedDB: indexedDBSize,
      cacheAPI: cacheAPISize,
      total
    });
  };

  // Load cache sizes on component mount
  useEffect(() => {
    calculateCacheSizes();
  }, []);

  // Format bytes to human readable format
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Clear frontend cache (localStorage, sessionStorage, IndexedDB, etc.)
  const clearFrontendCache = async () => {
    setCacheStatus(prev => ({ ...prev, frontend: 'clearing' }));
    const loadingToastId = showLoadingToast('Clearing frontend cache...');

    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear IndexedDB if available (with timeout to prevent hanging)
      if ('indexedDB' in window) {
        try {
          // Use a timeout to prevent infinite waiting
          const clearIndexedDB = new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              console.warn('IndexedDB clearing timed out');
              resolve();
            }, 5000); // 5 second timeout

            if ('databases' in indexedDB) {
              indexedDB.databases().then(databases => {
                const deletePromises = databases.map(db => {
                  if (db.name) {
                    return new Promise<void>((resolveDelete) => {
                      const deleteReq = indexedDB.deleteDatabase(db.name!);
                      deleteReq.onsuccess = () => resolveDelete();
                      deleteReq.onerror = () => {
                        console.warn(`Failed to delete database: ${db.name}`);
                        resolveDelete();
                      };
                    });
                  }
                  return Promise.resolve();
                });
                
                Promise.all(deletePromises).then(() => {
                  clearTimeout(timeout);
                  resolve();
                }).catch(() => {
                  clearTimeout(timeout);
                  resolve();
                });
              }).catch(() => {
                clearTimeout(timeout);
                resolve();
              });
            } else {
              clearTimeout(timeout);
              resolve();
            }
          });
          
          await clearIndexedDB;
        } catch (error) {
          console.warn('Could not clear IndexedDB:', error);
        }
      }

      // Clear Cache API if available
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (error) {
          console.warn('Could not clear Cache API:', error);
        }
      }

      // Clear service worker cache
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(registration => registration.unregister())
          );
        } catch (error) {
          console.warn('Could not clear service workers:', error);
        }
      }

      // Recalculate cache sizes after clearing
      await calculateCacheSizes();

      setCacheStatus(prev => ({ ...prev, frontend: 'success' }));
      dismissToast(loadingToastId);
      showSuccessToast('Frontend cache cleared successfully!');
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error clearing frontend cache:', error);
      setCacheStatus(prev => ({ ...prev, frontend: 'error' }));
      dismissToast(loadingToastId);
      showErrorToast('Failed to clear frontend cache');
    }
  };

  // Clear backend cache via API
  const clearBackendCache = async () => {
    setCacheStatus(prev => ({ ...prev, backend: 'clearing' }));
    const loadingToastId = showLoadingToast('Clearing backend cache...');

    try {
      if (isAdmin) {
        await api.post('/admin/clear-cache');
        setCacheStatus(prev => ({ ...prev, backend: 'success' }));
        dismissToast(loadingToastId);
        showSuccessToast('Backend cache cleared successfully!');
      } else {
        // For non-admin users, show a message that they can only clear frontend cache
        setCacheStatus(prev => ({ ...prev, backend: 'error' }));
        dismissToast(loadingToastId);
        showErrorToast('Backend cache clearing requires admin privileges');
      }
      
    } catch (error) {
      console.error('Error clearing backend cache:', error);
      setCacheStatus(prev => ({ ...prev, backend: 'error' }));
      dismissToast(loadingToastId);
      showErrorToast('Failed to clear backend cache');
    }
  };

  // Force app reinstall/reinitialize
  const forceReinstall = async () => {
    setCacheStatus(prev => ({ ...prev, reinstall: 'processing' }));
    const loadingToastId = showLoadingToast('Reinitializing app...');

    try {
      // Clear frontend cache first
      await clearFrontendCache();
      
      // Clear backend cache if user is admin
      if (isAdmin) {
        await clearBackendCache();
      }
      
      // Clear app data
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          await navigator.storage.persist();
          // Note: navigator.storage.clear() is not available in all browsersn          // await navigator.storage.clear();
        } catch (error) {
          console.warn('Could not clear storage quota:', error);
        }
      }

      // For PWA, try to update the service worker
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.update();
          }
        } catch (error) {
          console.warn('Could not update service worker:', error);
        }
      }

      setCacheStatus(prev => ({ ...prev, reinstall: 'success' }));
      dismissToast(loadingToastId);
      showSuccessToast('App reinitialized successfully! Reloading...');
      
      // Force a hard reload
      setTimeout(() => {
        window.location.href = window.location.href;
      }, 2000);

    } catch (error) {
      console.error('Error reinitializing app:', error);
      setCacheStatus(prev => ({ ...prev, reinstall: 'error' }));
      dismissToast(loadingToastId);
      showErrorToast('Failed to reinitialize app');
    }
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'clearing':
      case 'processing':
        return <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'clearing':
        return 'Clearing...';
      case 'processing':
        return 'Processing...';
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      default:
        return 'Ready';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Database className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Cache Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage app cache and force updates
              </p>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                  Important Notice
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  These actions will clear cached data and may require you to log in again. 
                  Make sure to backup any important data before proceeding.
                </p>
              </div>
            </div>
          </div>

          {/* Main Actions */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
                         {/* Frontend Cache */}
             <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
               <div className="flex items-center gap-3 mb-3">
                 <Smartphone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                 <h3 className="font-semibold text-gray-900 dark:text-white">
                   Frontend Cache
                 </h3>
                 {getStatusIcon(cacheStatus.frontend)}
               </div>
               <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                 Clear browser cache, localStorage, sessionStorage, and service worker cache
               </p>
               
               {/* Cache Size Display */}
               <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded border">
                 <div className="flex items-center justify-between mb-2">
                   <div className="flex items-center gap-2">
                     <HardDrive className="w-4 h-4 text-gray-500" />
                     <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Cache Sizes:</span>
                   </div>
                   <button
                     onClick={calculateCacheSizes}
                     className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                     title="Refresh cache sizes"
                   >
                     <RefreshCw className="w-3 h-3" />
                   </button>
                 </div>
                 <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                   <div>localStorage: {formatBytes(cacheSizes.localStorage)}</div>
                   <div>sessionStorage: {formatBytes(cacheSizes.sessionStorage)}</div>
                   <div>IndexedDB: {formatBytes(cacheSizes.indexedDB)}</div>
                   <div>Cache API: {formatBytes(cacheSizes.cacheAPI)}</div>
                 </div>
                 <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                   <div className="flex justify-between items-center">
                     <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">Total:</span>
                     <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{formatBytes(cacheSizes.total)}</span>
                   </div>
                 </div>
               </div>
               
               <div className="flex items-center justify-between">
                 <span className="text-xs text-gray-500 dark:text-gray-400">
                   Status: {getStatusText(cacheStatus.frontend)}
                 </span>
                 <button
                   onClick={clearFrontendCache}
                   disabled={cacheStatus.frontend === 'clearing'}
                   className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                 >
                   <Trash2 className="w-4 h-4" />
                   Clear
                 </button>
               </div>
             </div>

            {/* Backend Cache */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Server className="w-6 h-6 text-green-600 dark:text-green-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Backend Cache
                </h3>
                {getStatusIcon(cacheStatus.backend)}
                {!isAdmin && (
                  <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded">
                    Admin Only
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {isAdmin 
                  ? 'Clear Laravel application cache, route cache, and config cache'
                  : 'Backend cache clearing requires administrator privileges'
                }
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Status: {getStatusText(cacheStatus.backend)}
                </span>
                <button
                  onClick={clearBackendCache}
                  disabled={cacheStatus.backend === 'clearing'}
                  className={`px-3 py-1.5 text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${
                    isAdmin 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </button>
              </div>
            </div>

            {/* Force Reinstall */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <RefreshCw className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Force Reinstall
                </h3>
                {getStatusIcon(cacheStatus.reinstall)}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Complete app reinitialization with cache clearing and service worker update
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Status: {getStatusText(cacheStatus.reinstall)}
                </span>
                <button
                  onClick={forceReinstall}
                  disabled={cacheStatus.reinstall === 'processing'}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reinstall
                </button>
              </div>
            </div>
          </div>



          {/* Advanced Options */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              <span className="font-medium">Advanced Options</span>
              <RefreshCw className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Clear All Caches
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Clear both frontend and backend caches simultaneously
                  </p>
                                     <button
                     onClick={async () => {
                       if (isAdmin) {
                         await Promise.all([clearFrontendCache(), clearBackendCache()]);
                       } else {
                         await clearFrontendCache();
                         showErrorToast('Backend cache clearing requires admin privileges');
                       }
                     }}
                     disabled={cacheStatus.frontend === 'clearing' || cacheStatus.backend === 'clearing'}
                     className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                   >
                     <Trash2 className="w-4 h-4" />
                     Clear All Caches {!isAdmin && '(Frontend Only)'}
                   </button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    App Information
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <div>User Agent: {navigator.userAgent}</div>
                    <div>Platform: {navigator.platform}</div>
                    <div>Language: {navigator.language}</div>
                    <div>Online: {navigator.onLine ? 'Yes' : 'No'}</div>
                    <div>Service Worker: {'serviceWorker' in navigator ? 'Supported' : 'Not Supported'}</div>
                    <div>Cache API: {'caches' in window ? 'Supported' : 'Not Supported'}</div>
                    <div>IndexedDB: {'indexedDB' in window ? 'Supported' : 'Not Supported'}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
